import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Reports the intrinsic height of its content via a ResizeObserver. Used in the
 * read-only view to size text blocks to their rendered height instead of the
 * stale height stored from the editor. The wrapper is height:auto so its own box
 * reflects the content rather than the fixed height react-grid-layout imposes on
 * the grid item above it.
 *
 * @param onHeightChange called with the rendered content height in pixels whenever it changes
 */
export const MeasuredContent = ({
  onHeightChange,
  children,
}: {
  onHeightChange: (pixels: number) => void;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (element == null || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => onHeightChange(element.offsetHeight));
    observer.observe(element);

    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <div ref={ref} className="measured-content">
      {children}
    </div>
  );
};
